import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, Button, Input, Label } from "@learn-me/ui";
import { useCourseWizard } from "@/hooks";
import { adminApi } from "../../api/adminApi";
import { api } from "@/lib/api";
import type { ApiResponse } from "@learn-me/types";
import {
  DollarSign,
  Gift,
  Loader2,
  CheckCircle,
  Rocket,
  AlertCircle,
  Image,
  ExternalLink,
  Youtube,
  Tag,
  Check,
} from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

interface Category {
  id: string;
  name: string;
  slug: string;
  isMainArea: boolean;
}

interface WizardStep6PublishProps {
  onFinish: () => void;
}

export function WizardStep6Publish({ onFinish }: WizardStep6PublishProps) {
  const navigate = useNavigate();
  const {
    isFree,
    setIsFree,
    price,
    setPrice,
    currency,
    setCurrency,
    categoryIds,
    setCategoryIds,
    courseId,
    setCourseId,
    courseTitle,
    courseDescription,
    shortDescription,
    level,
    generatedChapters,
    reset,
  } = useCourseWizard();

  const [publishAs, setPublishAs] = useState<"DRAFT" | "PUBLISHED">("DRAFT");
  const [isCreating, setIsCreating] = useState(false);
  const [creationProgress, setCreationProgress] = useState("");

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const response = await api.get<ApiResponse<Category[]>>("/categories");
      return response.data.data || [];
    },
  });

  const handleCategoryToggle = (categoryId: string) => {
    if (categoryIds.includes(categoryId)) {
      setCategoryIds(categoryIds.filter((id) => id !== categoryId));
    } else if (categoryIds.length < 5) {
      setCategoryIds([...categoryIds, categoryId]);
    } else {
      toast.error("Maximo 5 categorias por curso");
    }
  };

  const handleCreate = async () => {
    setIsCreating(true);

    // Logging para debug
    console.log("[Wizard Step 6] Starting course creation");
    console.log(
      "[Wizard Step 6] generatedChapters count:",
      generatedChapters.length,
    );
    generatedChapters.forEach((ch, i) => {
      console.log(`  Chapter ${i + 1}: ${ch.title}`);
      console.log(`    - Sections: ${ch.sections?.length || 0}`);
      console.log(`    - Quiz: ${ch.quiz ? "Yes" : "No"}`);
    });

    try {
      // Step 1: Create the course
      setCreationProgress("Creando curso...");
      const course = await adminApi.createCourse({
        title: courseTitle,
        description: courseDescription,
        shortDescription,
        isFree,
        salePrice: isFree ? undefined : (price ?? undefined),
        currency,
        level,
        status: publishAs,
      });

      // Step 2: Update categories if any selected
      if (categoryIds.length > 0) {
        setCreationProgress("Asignando categorias...");
        await adminApi.updateCourseCategories(course.id, categoryIds);
      }

      // Step 3: Create chapters with sections and quizzes
      for (let i = 0; i < generatedChapters.length; i++) {
        const chapter = generatedChapters[i];
        if (!chapter) continue;

        setCreationProgress(
          `Creando capitulo ${i + 1} de ${generatedChapters.length}...`,
        );

        // Create chapter
        const newChapter = await adminApi.createChapter({
          courseId: course.id,
          title: chapter.title,
          description: chapter.description,
          position: i + 1,
          isFree: i === 0, // First chapter is free preview
          isPublished: publishAs === "PUBLISHED",
        });

        // Create sections
        if (chapter.sections && chapter.sections.length > 0) {
          console.log(
            `[Wizard Step 6] Creating ${chapter.sections.length} sections for chapter ${i + 1}`,
          );
          setCreationProgress(`Creando secciones del capitulo ${i + 1}...`);
          for (let j = 0; j < chapter.sections.length; j++) {
            const section = chapter.sections[j];
            if (!section) continue;

            await adminApi.createSection(newChapter.id, {
              title: section.title,
              content: section.content,
              position: j + 1,
            });
          }
        } else {
          console.warn(`[Wizard Step 6] NO SECTIONS for chapter ${i + 1}!`);
        }

        // Create quiz if exists
        if (chapter.quiz && chapter.quiz.questions.length > 0) {
          setCreationProgress(`Creando quiz del capitulo ${i + 1}...`);
          await adminApi.createQuiz({
            chapterId: newChapter.id,
            title: chapter.quiz.title || `Quiz: ${chapter.title}`,
            questions: chapter.quiz.questions.map((q) => ({
              text: q.text,
              explanation: q.explanation,
              options: q.options,
            })),
          });
        }
      }

      setCourseId(course.id);
      toast.success(
        publishAs === "PUBLISHED"
          ? "¡Curso publicado exitosamente!"
          : "Curso creado como borrador",
      );
    } catch (error) {
      console.error("Error creating course:", error);
      toast.error(
        error instanceof Error ? error.message : "Error al crear el curso",
      );
    } finally {
      setIsCreating(false);
      setCreationProgress("");
    }
  };

  const handleGoToEditor = () => {
    reset();
    if (courseId) {
      navigate(`/admin/courses/${courseId}`);
    }
  };

  const handleGoToCourses = () => {
    reset();
    navigate("/admin/courses");
  };

  const totalDuration = generatedChapters.reduce(
    (acc, ch) => acc + ch.estimatedDuration,
    0,
  );

  // Success state
  if (courseId) {
    return (
      <div className="max-w-xl mx-auto text-center">
        <Card>
          <CardContent className="pt-8 pb-10">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              ¡Curso creado exitosamente!
            </h2>
            <p className="text-gray-600 mb-6">
              Tu curso "{courseTitle}" ha sido creado
              {publishAs === "PUBLISHED" ? " y publicado" : " como borrador"}.
              Puedes agregar videos de YouTube a cada capitulo desde el editor.
            </p>

            <div className="flex flex-col gap-3">
              <Button onClick={handleGoToEditor} className="w-full">
                <ExternalLink className="h-4 w-4 mr-2" />
                Ir al editor del curso
              </Button>
              <Button
                variant="outline"
                onClick={handleGoToCourses}
                className="w-full"
              >
                Ver todos los cursos
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Course summary */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium text-gray-900 mb-4">Resumen del curso</h3>

          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Titulo:</span>
              <span className="font-medium text-gray-900">{courseTitle}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Capitulos:</span>
              <span className="font-medium text-gray-900">
                {generatedChapters.length}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Duracion total:</span>
              <span className="font-medium text-gray-900">
                {totalDuration} minutos
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Quizzes:</span>
              <span className="font-medium text-gray-900">
                {generatedChapters.filter((ch) => ch.includeQuiz).length}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Categories */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="h-5 w-5 text-gray-600" />
            <h3 className="font-medium text-gray-900">Categorias</h3>
            <span className="text-xs text-gray-500">(max. 5)</span>
          </div>

          {categories.length === 0 ? (
            <p className="text-sm text-gray-500">
              No hay categorias disponibles
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => {
                const isSelected = categoryIds.includes(category.id);
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleCategoryToggle(category.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                      isSelected
                        ? "bg-primary-100 text-primary-700 border-2 border-primary-500"
                        : "bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200"
                    }`}
                  >
                    {isSelected && <Check className="h-3.5 w-3.5" />}
                    {category.name}
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium text-gray-900 mb-4">Precio del curso</h3>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              type="button"
              onClick={() => setIsFree(true)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                isFree
                  ? "border-green-500 bg-green-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Gift
                  className={`h-5 w-5 ${isFree ? "text-green-600" : "text-gray-500"}`}
                />
                <span
                  className={`font-medium ${isFree ? "text-green-700" : "text-gray-900"}`}
                >
                  Gratuito
                </span>
              </div>
              <p className="text-sm text-gray-500">
                Cualquiera puede acceder sin pagar
              </p>
            </button>

            <button
              type="button"
              onClick={() => setIsFree(false)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                !isFree
                  ? "border-primary-500 bg-primary-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <DollarSign
                  className={`h-5 w-5 ${!isFree ? "text-primary-600" : "text-gray-500"}`}
                />
                <span
                  className={`font-medium ${!isFree ? "text-primary-700" : "text-gray-900"}`}
                >
                  De pago
                </span>
              </div>
              <p className="text-sm text-gray-500">
                Los estudiantes deben pagar para acceder
              </p>
            </button>
          </div>

          {!isFree && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label htmlFor="price">Precio</Label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="price"
                      type="number"
                      min="1"
                      step="0.01"
                      value={price || ""}
                      onChange={(e) =>
                        setPrice(
                          e.target.value ? parseFloat(e.target.value) : null,
                        )
                      }
                      className="pl-9"
                      placeholder="29.99"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="currency">Moneda</Label>
                  <select
                    id="currency"
                    value={currency}
                    onChange={(e) =>
                      setCurrency(e.target.value as "USD" | "CRC")
                    }
                    className="mt-1 w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="CRC">CRC (₡)</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Publish options */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium text-gray-900 mb-4">
            Modo de publicacion
          </h3>

          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setPublishAs("DRAFT")}
              className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                publishAs === "DRAFT"
                  ? "border-primary-500 bg-primary-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <span
                className={`font-medium ${publishAs === "DRAFT" ? "text-primary-700" : "text-gray-900"}`}
              >
                Guardar como borrador
              </span>
              <p className="text-sm text-gray-500 mt-1">
                El curso no sera visible para estudiantes. Podras editarlo y
                publicarlo despues.
              </p>
            </button>

            <button
              type="button"
              onClick={() => setPublishAs("PUBLISHED")}
              className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                publishAs === "PUBLISHED"
                  ? "border-green-500 bg-green-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <span
                className={`font-medium ${publishAs === "PUBLISHED" ? "text-green-700" : "text-gray-900"}`}
              >
                Publicar inmediatamente
              </span>
              <p className="text-sm text-gray-500 mt-1">
                El curso estara disponible para estudiantes inmediatamente.
              </p>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Validation notice */}
      {!isFree && (!price || price <= 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Precio requerido
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Debes establecer un precio mayor a $0 para un curso de pago.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create button */}
      <Button
        onClick={handleCreate}
        disabled={isCreating || (!isFree && (!price || price <= 0))}
        className="w-full py-6 text-lg"
      >
        {isCreating ? (
          <>
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            {creationProgress || "Creando curso..."}
          </>
        ) : (
          <>
            <Rocket className="h-5 w-5 mr-2" />
            {publishAs === "PUBLISHED"
              ? "Crear y publicar curso"
              : "Crear curso como borrador"}
          </>
        )}
      </Button>

      {/* Notes */}
      <div className="space-y-3">
        <div className="bg-gray-50 rounded-lg p-4 flex gap-3">
          <Image className="h-5 w-5 text-gray-500 flex-shrink-0" />
          <p className="text-sm text-gray-600">
            Podras agregar una imagen de portada despues de crear el curso desde
            el editor.
          </p>
        </div>

        <div className="bg-blue-50 rounded-lg p-4 flex gap-3">
          <Youtube className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-700">
            Los videos de YouTube/Vimeo se agregan a cada capitulo
            individualmente desde el editor de capitulos. Esto te permite tener
            videos diferentes para cada tema.
          </p>
        </div>
      </div>
    </div>
  );
}
